import type { Variants } from 'framer-motion';
import type { CSSProperties } from 'react';
import type { IDimOverlayProps } from './DimOverlay.interface';
import styles from './DimOverlay.module.css';

const DEFAULT_ANIMATION_PERIOD_SHOW = 0.4;
const DEFAULT_ANIMATION_PERIOD_HIDE = 0.3;

export enum StyleElem {
    Container = 'Container',
}

export enum IDimOverlayState {
    Dimmed = 'Dimmed',
    Hidden = 'Hidden',
}

const DEFAULTS: Record<StyleElem, CSSProperties> = {
    [StyleElem.Container]: {},
};

const RUNTIME: Record<StyleElem, Record<IDimOverlayState, CSSProperties>> = {
    [StyleElem.Container]: {
        [IDimOverlayState.Dimmed]: {
            backgroundColor: 'rgba(0,0,0,0.7)',
        },
        [IDimOverlayState.Hidden]: {
            backgroundColor: 'rgba(0,0,0,0.)',
        },
    },
};

const PROPS_APPLICATOR: Record<StyleElem, (props: IDimOverlayProps) => CSSProperties> = {
    [StyleElem.Container]: (props: IDimOverlayProps) => ({
        ...RUNTIME[StyleElem.Container][
            props.isDim ? IDimOverlayState.Dimmed : IDimOverlayState.Hidden
        ],
        zIndex: props.zIndex ?? 0,
        pointerEvents: props.isDim ? 'auto' : 'none',
    }),
};

export function animationVariantsForElement(elem: StyleElem): Variants {
    const variants = {
        [IDimOverlayState.Dimmed]: {
            ...RUNTIME[elem][IDimOverlayState.Dimmed],
            transition: { type: 'tween', duration: DEFAULT_ANIMATION_PERIOD_SHOW },
        },
        [IDimOverlayState.Hidden]: {
            ...RUNTIME[elem][IDimOverlayState.Hidden],
            transition: { type: 'tween', duration: DEFAULT_ANIMATION_PERIOD_HIDE },
        },
    };

    /* Cannot Pass CSSProperties to Variants due to 'Omit' in CSSPropertiesWithoutTransitionOrSingleTransforms */
    return variants as Variants;
}

export function animationStateForElement(elem: StyleElem, props: IDimOverlayProps): string {
    return props.isDim ?? false ? IDimOverlayState.Dimmed : IDimOverlayState.Hidden;
}

export function classNameForElement(element: StyleElem, model: IDimOverlayProps): string {
    const name = element.toString();
    const classLoad = styles[name];
    return classLoad || '';
}

export function styleForElement(
    element: StyleElem,
    props: IDimOverlayProps,
    overrides: CSSProperties = {},
): CSSProperties {
    const style = { ...DEFAULTS[element], ...PROPS_APPLICATOR[element](props), ...overrides };

    return style;
}
